import type { ResolverPlayerStats, ResolverRankedPlayer } from '../placeholder';
import { PlayerRankingStat, type PlayerRankingSettings } from './player-ranking-settings';

const STAT_ACCESSOR: Readonly<Record<PlayerRankingStat, (s: ResolverPlayerStats) => number>> = {
  [PlayerRankingStat.GAMES]: (s) => s.games,
  [PlayerRankingStat.GOALS]: (s) => s.goals,
  [PlayerRankingStat.ASSISTS]: (s) => s.assists,
  [PlayerRankingStat.GOALS_ASSISTS]: (s) => s.goals + s.assists,
  [PlayerRankingStat.YELLOW_CARDS]: (s) => s.yellowCards,
  [PlayerRankingStat.RED_CARDS]: (s) => s.redCards,
};

/**
 * Sorts players by the chosen stat and slices to top-N.
 *
 * The tie-break chain is total — it ends on `player.id`, which no two stats
 * lines share — so the result never depends on the backend's row order or on
 * sort stability. That matters because this feeds an image: two renders of the
 * same championship must produce the same picture, and without the id tie-break
 * a backend that reordered its rows would silently swap ranks 2 and 3 between
 * renders.
 *
 * Chain: the stat → fewer games played (the golden-boot rule: same goals in
 * fewer games ranks higher; for cards it surfaces the worse offender) → name →
 * id. The chain is a product policy and lives only here.
 */
export function rankPlayers(
  players: readonly ResolverRankedPlayer[],
  ranking: PlayerRankingSettings,
): readonly ResolverRankedPlayer[] {
  const get = STAT_ACCESSOR[ranking.stat];
  const dir = ranking.direction === 'asc' ? 1 : -1;

  const sorted = [...players].sort((a, b) => {
    const primary = (get(a.stats) - get(b.stats)) * dir;
    if (primary !== 0) return primary;
    const byGames = a.stats.games - b.stats.games;
    if (byGames !== 0) return byGames;
    const byName = a.player.name.localeCompare(b.player.name, 'ru');
    if (byName !== 0) return byName;
    return a.player.id - b.player.id;
  });

  return ranking.limit && ranking.limit > 0 ? sorted.slice(0, ranking.limit) : sorted;
}
